import { useState, useEffect, useRef, useCallback } from "react";

const EXIT_ANIMATION_MS = 250;

/**
 * Tracks items leaving a list so they can play an exit animation
 * before being removed from the DOM. Exiting items are kept separate
 * from the active list so they don't interfere with Draggable indices.
 */
export function useAnimatedList(visibleIds: string[]) {
  const prevIdsRef = useRef<string[]>(visibleIds);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const [exitingIds, setExitingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const current = new Set(visibleIds);
    const removed = prevIdsRef.current.filter((id) => !current.has(id));

    // Items that reappeared before their exit animation finished
    const restored = visibleIds.filter((id) => exitingIds.has(id));
    if (restored.length > 0) {
      restored.forEach((id) => {
        const timer = timersRef.current.get(id);
        if (timer) {
          clearTimeout(timer);
          timersRef.current.delete(id);
        }
      });
      setExitingIds((prev) => {
        const next = new Set(prev);
        restored.forEach((id) => next.delete(id));
        return next;
      });
    }

    if (removed.length > 0) {
      setExitingIds((prev) => {
        const next = new Set(prev);
        removed.forEach((id) => next.add(id));
        return next;
      });

      removed.forEach((id) => {
        const timer = setTimeout(() => {
          timersRef.current.delete(id);
          setExitingIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }, EXIT_ANIMATION_MS);
        timersRef.current.set(id, timer);
      });
    }

    prevIdsRef.current = visibleIds;
  }, [visibleIds]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  const isExiting = useCallback(
    (id: string) => exitingIds.has(id),
    [exitingIds]
  );

  return { exitingIds: Array.from(exitingIds), isExiting };
}
